import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonLabel,
  IonModal,
  IonRow,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import { close } from 'ionicons/icons';
import { isEmpty } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { Maybe } from '../../api/graphql/generated';
import {
  InfiniteScrollOptions,
  QueryInput,
  useListQueryWithInfiniteScroll,
} from '../../api/hooks/infinite-scroll.hooks';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { useWithConfirmation } from '../button/with-confirmation';
import { IonItem } from '../ionic/IonItem';
import { Searchbar, SearchbarRef } from '../ui/searchbar/searchbar';
import './ion-form-picklist-modal.css';

export interface IonFormPicklistModalObjectType {
  id: string;
}

export interface IonFormPicklistModalInputProps {
  inputPlaceholder?: string;
}

export interface IonFormPicklistModalProps {
  modalHeader: string;
  selectedObjectsHeader?: string;
  availableObjectsHeader?: string;
  deselectConfirmationHeader?: string;
  deselectConfirmationButtonText?: string;
  noSelectedObjectsMessage?: string;
  noAvailableObjectsMessage?: string;
  searchbarPlaceholder?: string;
}

export interface IonFormPicklistModalFieldProps<
  ObjectType extends IonFormPicklistModalObjectType,
  ListObjectQuery,
> extends FieldConfig {
  className?: string;
  label: string;
  useQuery: InfiniteScrollOptions['useQuery'];
  getObjectsFromListQuery: (
    data?: ListObjectQuery
  ) => ObjectType[] | undefined | null;
  getObjectDisplayName: (object: ObjectType) => string;
  inputProps: IonFormPicklistModalInputProps;
  modalProps: IonFormPicklistModalProps;
  isEmptyAll?: boolean;
}

const IonFormPicklistModalField = <
  ObjectType extends IonFormPicklistModalObjectType,
  ListObjectQuery,
  ListObjectInput extends QueryInput & {
    searchTerm?: Maybe<string>;
  },
>(
  props: IonFormPicklistModalFieldProps<ObjectType, ListObjectQuery>
): ReactElement => {
  const {
    label,
    useQuery,
    getObjectsFromListQuery,
    getObjectDisplayName,
    inputProps,
    modalProps,
    isEmptyAll,
    ...otherProps
  } = props;
  const { inputPlaceholder } = inputProps;
  const {
    modalHeader,
    selectedObjectsHeader,
    availableObjectsHeader,
    deselectConfirmationHeader,
    deselectConfirmationButtonText,
    noSelectedObjectsMessage,
    noAvailableObjectsMessage,
    searchbarPlaceholder,
  } = modalProps;

  const [showModal, setShowModal] = useState(false);
  const searchbarRef = useRef<SearchbarRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField<ObjectType[]>(otherProps as any);

  const errorText = <IonText color="danger">{meta.error}</IonText>;

  const [searchText, setSearchText] = useState<string>();
  const {
    infiniteScroll,
    result: { data: listQueryData },
  } = useListQueryWithInfiniteScroll<ListObjectQuery, ListObjectInput>({
    useQuery,
    input: {
      searchTerm: searchText,
    } as ListObjectInput,
    queryOptions: {
      fetchPolicy: 'cache-and-network',
    },
  });
  const objects = getObjectsFromListQuery(listQueryData) ?? [];
  const selectedObjects = useMemo(() => field.value ?? [], [field.value]);
  const assignedObjects = selectedObjects.map((obj) => obj.id);
  const availableObjects = objects?.filter((obj) => {
    return !assignedObjects.includes(obj.id);
  });

  const withConfirmation = useWithConfirmation({
    header: deselectConfirmationHeader,
    destructivePrompt: deselectConfirmationButtonText,
  });

  const onDeselect = useCallback(
    (obj: ObjectType) => {
      if (deselectConfirmationHeader) {
        return () => {
          withConfirmation({
            onConfirmation: () => {
              helper.setValue(
                selectedObjects.filter((selected) => selected.id !== obj.id)
              );
            },
          });
        };
      }

      return () => {
        helper.setValue(
          selectedObjects.filter((selected) => selected.id !== obj.id)
        );
      };
    },
    [deselectConfirmationHeader, helper, selectedObjects, withConfirmation]
  );

  const maxDisplay = 3;
  const inputValue = useMemo(() => {
    if (isEmpty(field.value)) {
      return isEmptyAll ? getLocalizedText('common:all') : undefined;
    }

    const head = field.value.slice(0, maxDisplay);

    return head.map(getObjectDisplayName).join(', ');
  }, [field.value, getObjectDisplayName, isEmptyAll]);

  const endText = useMemo(() => {
    if (isEmpty(field.value)) {
      return undefined;
    }

    if (field.value.length > maxDisplay) {
      return getLocalizedText('common:plus_num_more', {
        num: field.value.length - maxDisplay,
      });
    }
  }, [field.value]);

  return (
    <IonItem
      lines={'full'}
      className={props.className}
      button={true}
      onClick={(e) => {
        e.nativeEvent.stopImmediatePropagation();
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
      }}
    >
      <IonInput
        className="select-modal-field"
        aria-label={label}
        labelPlacement="stacked"
        placeholder={
          inputPlaceholder ?? getLocalizedText('common:click_to_select')
        }
        type="text"
        value={inputValue}
      >
        <div slot="label">
          <IonLabel color={'primary'}>
            {label} {errorText}
          </IonLabel>
        </div>
        <div slot="end">
          <IonText color={'medium'}>{endText}</IonText>
        </div>
      </IonInput>
      <IonModal isOpen={showModal} className="form-select-modal">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal(false);
                }}
              >
                <IonIcon slot="icon-only" icon={close} />
              </IonButton>
              <IonTitle>{modalHeader}</IonTitle>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <Searchbar
            ref={searchbarRef}
            ionSearchbarProps={{
              placeholder:
                searchbarPlaceholder ?? getLocalizedText('common:search'),
            }}
            onSearch={setSearchText}
          />
          <IonAccordionGroup value={'selected'}>
            <IonAccordion value="selected">
              <IonItem
                slot="header"
                className="objects-group-header transparent-background"
              >
                <IonLabel>
                  {selectedObjectsHeader ?? getLocalizedText('common:selected')}
                </IonLabel>
              </IonItem>
              <div slot="content">
                {selectedObjects.length ? (
                  selectedObjects.map((obj, idx) => {
                    const isLast = idx === selectedObjects.length - 1;
                    return (
                      <IonItem
                        key={obj.id}
                        onClick={onDeselect(obj)}
                        button={true}
                        lines={isLast ? 'none' : 'full'}
                      >
                        <IonLabel>{getObjectDisplayName(obj)}</IonLabel>
                      </IonItem>
                    );
                  })
                ) : (
                  <EmptyObjectsMessage
                    message={
                      noSelectedObjectsMessage ??
                      getLocalizedText('common:none_selected') ??
                      'None selected'
                    }
                  />
                )}
              </div>
            </IonAccordion>
          </IonAccordionGroup>

          <IonAccordionGroup value={'available'}>
            <IonAccordion value="available">
              <IonItem
                slot="header"
                className="objects-group-header transparent-background"
              >
                <IonLabel>
                  {availableObjectsHeader ??
                    getLocalizedText('common:available')}
                </IonLabel>
              </IonItem>
              <div slot="content">
                {availableObjects?.length ? (
                  availableObjects?.map((obj, idx) => {
                    const isLast = idx === availableObjects.length - 1;
                    return (
                      <IonItem
                        key={obj.id}
                        onClick={() => {
                          helper.setValue([...selectedObjects, obj]);
                        }}
                        button={true}
                        lines={isLast ? 'none' : 'full'}
                      >
                        <IonLabel>{getObjectDisplayName(obj)}</IonLabel>
                      </IonItem>
                    );
                  })
                ) : (
                  <EmptyObjectsMessage
                    message={
                      noAvailableObjectsMessage ??
                      getLocalizedText('common:none_available') ??
                      'None available'
                    }
                  />
                )}
                {infiniteScroll}
              </div>
            </IonAccordion>
          </IonAccordionGroup>
        </IonContent>
      </IonModal>
    </IonItem>
  );
};

interface EmptyObjectsMessageProps {
  message: string;
}

const EmptyObjectsMessage = (props: EmptyObjectsMessageProps): ReactElement => {
  const { message } = props;

  return (
    <IonGrid className="ion-text-center">
      <IonRow>
        <IonCol>
          <IonText>{message}</IonText>
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

export { IonFormPicklistModalField };
